import axios from 'axios';
import { clearQuestionStudyDrafts } from '../hooks/useQuestionStudyDraft';
import { clearRevisionReflectionDrafts } from '../hooks/useRevisionReflectionDraft';
import { clearProfileNameDrafts } from '../hooks/useProfileNameDraft';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import api from '@/lib/api';
import { verifyToken } from '@/lib/jwtUtils';

export interface InstitutionSelection {
  selectionToken: string;
  institutions: { tenantId: string; institutionName: string; roles: string[] }[];
}

interface User {
  id: string;
  username: string;
  email: string;
  name?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<InstitutionSelection | void>;
  selectInstitution: (selectionToken: string, tenantId: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateProfile: (name: string) => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const changed = (event: StorageEvent) => {
      if (event.key === 'token') { if (!event.newValue) { clearQuestionStudyDrafts(); clearProfileNameDrafts(); clearRevisionReflectionDrafts(); } window.location.assign('/'); }
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const validateAndSetAuth = async () => {
      console.log('AuthContext: Validating authentication...');
      const token = localStorage.getItem('token');
      const currentPath = location.pathname;

      if (token) {
        try {
          // Verify token is valid and not expired
          const payload = verifyToken(token);

          if (payload) {
            console.log('AuthContext: Token is valid, fetching user profile');

            // Fetch user profile to ensure user still exists and is active
            const response = await api.get('/auth/me');
            const userData = response.data;

            setUser({
              id: userData.id || payload.sub || payload.userId,
              username: userData.username || userData.email,
              email: userData.email,
              name: userData.name
            });

            // If we're on the login page, redirect to dashboard
            if (currentPath === '/login') {
              console.log('AuthContext: Already logged in, redirecting to dashboard');
              setTimeout(() => {
                navigate('/', { replace: true });
              }, 100);
            }

            setIsLoading(false);
            return;
          } else {
            console.error('AuthContext: Token is expired or invalid');

            // Clear the invalid token
            localStorage.removeItem('token');
            setUser(null);

            // If we're not on a public page, redirect to login
            const publicRoutes = ['/login', '/register', '/verify-email', '/setup', '/forgot-password', '/reset-password', '/password-reset'];
            if (!publicRoutes.includes(currentPath)) {
              console.log('AuthContext: Token invalid, redirecting to login');
              navigate('/login', { replace: true });
            }

            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('AuthContext: Token validation error:', error);
          // Only invalidate credentials when the server explicitly rejects them.
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            localStorage.removeItem('token');
            setUser(null);
          } else {
            toast({ title: 'Unable to verify session', description: 'Please check your connection and try again.', variant: 'destructive' });
          }
        }
      } else {
        console.log('AuthContext: No token found');
      }

      setIsLoading(false);
    };

    validateAndSetAuth();
  }, [location.pathname, navigate]);



  const finishLogin = (loginData: any) => {
      if (!loginData.token || !loginData.roles?.includes('ROLE_STUDENT')) throw new Error('This membership does not have Student portal access.');
      // Extract token and user data from LoginResponse
      const token = loginData.token;
      const user = {
        id: loginData.userId,
        username: loginData.username || loginData.email,
        email: loginData.email,
        name: loginData.name
      };

      localStorage.removeItem('tenantCode');
      localStorage.setItem('token', token);
      setUser(user);
      toast({
        title: 'Login Successful',
        description: `Welcome back, ${user.name || user.email}!`,
      });
      navigate('/');
  };

  const selectInstitution = async (selectionToken: string, tenantId: string) => {
    const { data } = await api.post('/auth/institutions/select', { selectionToken, tenantId });
    finishLogin(data);
  };

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/login', { email, password });
      const loginData = response.data;

      if (loginData.selectionToken) return loginData as InstitutionSelection;
      finishLogin(loginData);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (username: string, email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/register', { username, email, password });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      toast({
        title: 'Registration Successful',
        description: 'Your account has been created successfully!',
      });
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: 'Registration Failed',
        description: 'Could not create your account. Please try again.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (name: string) => {
    const response = await api.put('/auth/profile', { name });
    setUser(current => current ? { ...current, name: response.data.name } : current);
  };

  const logout = async () => {
    if (localStorage.getItem('token')) {
      try { await api.post('/auth/logout'); }
      catch (error: any) {
        if (![401, 403].includes(error?.response?.status)) {
          toast({ title: 'Sign out failed', description: 'Could not revoke your session. Please retry.', variant: 'destructive' });
          return;
        }
      }
    }
    clearProfileNameDrafts();
    clearRevisionReflectionDrafts();
    clearQuestionStudyDrafts();
    localStorage.removeItem('token');
    setUser(null);
    toast({
      title: 'Logged Out',
      description: 'You have been successfully logged out.',
    });
    navigate('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        selectInstitution,
        register,
        logout,
        updateProfile,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
